import React from "react";

const PressureVideo = ({ subsection, saveProgress }) => {
  const about = (
    <div className="space-y-5 pt-2 pb-6 text-body leading-7 text-[#484848]">
      <p>
      This lesson links executive presence to how you behave when stakes rise. It argues pressure starts in the mind, and that calm authority is read in the moment through pace, pauses, clear framing, and controlling the next step story. It introduces emotional detachment as the core skill, so self worth is not tied to outcomes. The result is composure that steadies others and makes you look promotable under stress.
      </p>
    </div>
  );

  const transcriptHtml = `
    <p><strong>Presence and pressure are interconnected.</strong> Your presence, like it or not, is determined by how you handle pressure.</p>

    <p>People often think handling pressure is about experience or skill. The truth is it starts in your head. If you can <strong>feel like a CEO</strong>, even if you've never led a team, you'll naturally act like one. Your posture, your voice, your decisions… everything changes. Others notice and suddenly, you look promotable.</p>

    <p>Even if you don't have a track record, what matters more is how you project authority in the moment. People pick up on calm, decisive energy, more than your resume.</p>

    <p><strong>Pressure itself is simple:</strong> it's what you feel when the stakes are high. Imagine walking on a plank on the ground, low stakes, seems easy. Now raise it ten feet, and suddenly every step tests you. The task hasn't changed, the stakes just went higher, and what you feel, that's pressure.</p>

    <p><strong>How do I show I can handle it?</strong></p>

    <p>Pause before you speak. Speak slowly. Frame your decisions clearly. And here's something most people miss: <strong>control the story.</strong> Calmly say what will happen next instead of reacting. Anchor yourself physically, feet steady on the ground, hands relaxed. These small signals tell everyone around you: '<em>I've got this</em>"</p>

    <h3 style="margin-top: 24px; margin-bottom: 12px; font-weight: 700; font-size: 1.125rem;">Here's a story that captures it perfectly:</h3>
    <p>A major consultancy once messed up a big client project. Tensions were sky-high, the client was furious, and they sent a senior leader to handle the situation. The client went off, yelling, blaming, venting. The leader listened, and then calmly said, "<strong>Everything you said, I already know. Do you want me to say sorry, or do you want me to fix it?</strong>"</p>

    <p>That's <strong>presence under pressure</strong>.</p>
    <p>He didn't react, didn't get defensive, he detached emotionally from the noise and focused on what mattered.</p>

    <p>That's the secret to staying calm in chaos: <strong>emotional detachment.</strong> You stop trying to please everyone or control every outcome. You stop caring about how they feel about you and start caring about what needs to be done.</p>

    <p>Pressure feels heavy only when your self-worth depends on the outcome. The moment you detach, when you say, "<em>I'll do my best, and whatever happens, happens</em>", that's when you truly free yourself.</p>

    <p>Start by treating small moments like they have your next level stakes. When real pressure hits, your brain is already ready.</p>

    <p>If you haven't watched the movie <strong>12th Fail</strong>, it's time you did. The final interview scene captures pressure beautifully. In his first interview, he's so consumed by pressure that his brain and eyes are focused inward, on himself. The second time, he's detached from the outcome. He looks around, reads the room, and responds in a way that puts others at ease.</p>

    <p>That's what <strong>true composure</strong> looks like, when you stop performing for approval and start observing to connect.</p>

    <p><strong>How do you define pressure?</strong></p>
  `;

  const transcript = (
    <div
      className="pt-2 pb-6 text-body leading-7 text-[#484848] max-w-2xl"
      dangerouslySetInnerHTML={{ __html: transcriptHtml }}
    />
  );

  // const relatedLinks = (
  //   <div className="pt-2 pb-6">
  //     <ul className="list-disc list-inside text-body space-y-2">
  //       <li>
  //         <button type="button" className="text-link">
  //           Download Slides
  //         </button>
  //       </li>
  //       <li>
  //         <button type="button" className="text-link">
  //           Reference Reading: Career Transitions Report 2025
  //         </button>
  //       </li>
  //     </ul>
  //   </div>
  // );

  const notes = (
    <div className="pt-2 pb-6">
      <textarea
        rows={4}
        placeholder="Write your notes here..."
        className="w-full border border-gray-200 rounded-xl p-3 text-body focus:ring-2 focus:ring-brand/40 focus:outline-none"
        onBlur={(e) => saveProgress("in_progress", { notes: e.target.value })}
      />
    </div>
  );

  return { about, transcript, notes };
};

export default PressureVideo;